import React from "react"
import { Routes, Route, Navigate } from "react-router-dom"
import PublicRoutes from './PublicRoutes'
import ProtectedRoutes from './ProtectedRoutes'
import Homepage from "../pages/Homepage"
import AboutPage from "@/pages/About"
import ContactPage from "@/pages/Contact"
import HelpCenterPage from "@/pages/HelpCenter"
import CoursesPage from "@/pages/Courses"
import BundlesPage from "@/pages/Bundles"
import PrivacyPolicyPage from "@/pages/PrivacyPolicy"
import TermsOfServicePage from "@/pages/TermsOfService"
import SignIn from "@/pages/SignIn"
import SignUp from "@/pages/Signup"
import ForgotPassword from "@/pages/ForgotPassword"
import ResetPassword from "@/pages/ResetPassword"
import DashboardLayout from "@/components/DashboardLayout"
import DashboardOverview from "@/pages/Dashboard/DashboardOverview"
import DashboardCourses from "@/pages/Dashboard/DashboardCourses"
import DashboardBundles from "@/pages/Dashboard/DashboardBundles"
import DashboardLearning from "@/pages/Dashboard/DashboardLearning"
import DashboardSettings from "@/pages/Dashboard/DashboardSettings"
import DashboardBilling from "@/pages/Dashboard/DashboardBilling"
import AdminDashboard from "@/pages/Admin/Dashboard"
import AdminProducts from "@/pages/Admin/Products"
import AdminVideos from "@/pages/Admin/Videos"
import AdminSiteVideos from "@/pages/Admin/SiteVideos"
import AdminEnrollments from "@/pages/Admin/Enrollments"
import AdminSystem from "@/pages/Admin/System"
import AdminUsers from "@/pages/Admin/Users"
import AdminWeb from "@/pages/Admin/Web"
import AdminDatabaseIntegrity from "@/pages/Admin/DatabaseIntegrity"
import AdminFiles from "@/pages/Admin/Files"
import AdminReviews from "@/pages/Admin/Reviews"
import AdminErrorLogs from "@/pages/Admin/ErrorLogs"
import Enrollment from "@/pages/Enrollment"
import Welcome from "@/pages/Welcome"
import Classroom from "@/pages/Classroom"
import PromptEngineeringMastery from "@/pages/PromptEngineeringMastery"
import AIToolsAccelerator from "@/pages/AIToolsAccelerator"
import AIEssentialsBundle from "@/pages/AIEssentialsBundle"
import UltimatePromptBundle from "@/pages/UltimatePromptBundle"
import DigitalProductCheckout from "@/pages/DigitalProductCheckout"
import DashboardFiles from "@/pages/Dashboard/DashboardFiles"
import Enroll from "@/pages/Enroll"

const AppRouter: React.FC = () => {
	return (
		<Routes>
			{/* Public Routes */}
			<Route path="/" element={<PublicRoutes />}>
				<Route path="/" element={<Homepage />} />
				<Route path="/about" element={<AboutPage />} />
				<Route path="/contact" element={<ContactPage />} />
				<Route path="/help" element={<HelpCenterPage />} />
				<Route path="/courses" element={<CoursesPage />} />
				<Route path="/bundles" element={<BundlesPage />} />
				<Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
				<Route path="/terms-of-service" element={<TermsOfServicePage />} />
				<Route path="/sign-in" element={<SignIn />} />
				<Route path="/sign-up" element={<SignUp />} />
				<Route path="/forgot-password" element={<ForgotPassword />} />
				<Route path="/reset-password" element={<ResetPassword />} />
				<Route path="/enrollment" element={<Enrollment />} />
				<Route path="/enroll/:productType/:productId" element={<Enroll />} />
				<Route path="/prompt-engineering-mastery" element={<PromptEngineeringMastery />} />
				<Route path="/ai-tools-accelerator" element={<AIToolsAccelerator />} />
				<Route path="/ai-essentials-bundle" element={<AIEssentialsBundle />} />
				<Route path="/ultimate-prompt-bundle" element={<UltimatePromptBundle />} />
				<Route path="/digital-product-checkout" element={<DigitalProductCheckout />} />

			</Route>

			{/* Protected Routes - Authenticated Users with Dashboard Layout */}
			<Route element={<ProtectedRoutes />}>
				<Route path="/welcome" element={<Welcome />} />
				<Route path="/classroom/course/:courseId" element={<Classroom />} />
				<Route path="/dashboard" element={<DashboardLayout />}>
					<Route index element={<DashboardOverview />} />
					<Route path="courses" element={<DashboardCourses />} />
					<Route path="bundles" element={<DashboardBundles />} />
					<Route path="learning" element={<DashboardLearning />} />
					<Route path="settings" element={<DashboardSettings />} />
					<Route path="billing" element={<DashboardBilling />} />
					<Route path="files" element={<DashboardFiles />} />
					<Route path="files/:productSlug" element={<DashboardFiles />} />
				</Route>
			</Route>

			{/* Protected Routes - Admin Only */}
			<Route element={<ProtectedRoutes roleRequired="admin" />}>
				<Route path="/admin/products" element={<AdminProducts />} />
				<Route path="/admin/videos" element={<AdminVideos />} />
				<Route path="/admin/files" element={<AdminFiles />} />
				<Route path="/admin/site-videos" element={<AdminSiteVideos />} />
				<Route path="/admin/enrollments" element={<AdminEnrollments />} />
				<Route path="/admin/database" element={<AdminDatabaseIntegrity />} />
				<Route path="/admin/users" element={<AdminUsers />} />
				<Route path="/admin/system" element={<AdminSystem />} />
				<Route path="/admin/web" element={<AdminWeb />} />
				<Route path="/admin/dashboard" element={<AdminDashboard />} />
				<Route path="/admin/reviews" element={<AdminReviews />} />
				<Route path="/admin/error-logs" element={<AdminErrorLogs />} />
				<Route path="/admin/error-logs/:errorId" element={<AdminErrorLogs />} />
			</Route>

			{/* Access Denied Route */}
			<Route path="/denied" element={
				<div className="min-h-screen bg-black text-white flex items-center justify-center">
					<div className="text-center">
						<h1 className="text-4xl font-bold mb-4 text-red-400">Access Denied</h1>
						<p className="text-gray-400 mb-8">You don't have permission to access this page.</p>
						<a href="/" className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-3 rounded-full font-semibold hover:shadow-xl transition-all">
							Go to Homepage
						</a>
					</div>
				</div>
			} />

			{/* Catch all - redirect to home */}
			<Route path="*" element={<Navigate to="/" replace />} />
		</Routes>
	)
}

export default AppRouter
